import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Typography } from "./Typography";
import { Button } from "@/components/forms/Button";
import { Select } from "@/components/forms/Select";
import { generatePageNumbers } from "@/lib/utils/pagination.utils";

export interface PaginationProps {
  /** Current page (1-based) */
  current: number;
  /** Total number of data items */
  total: number;
  /** Items per page (default: 10) */
  pageSize?: number;
  /** Called when page changes */
  onChange: (page: number) => void;

  /** Show page size changer dropdown (default: false) */
  showSizeChanger?: boolean;
  /** Options for size changer (default: [10, 25, 50, 100]) */
  pageSizeOptions?: number[];
  /** Called when page size changes */
  onPageSizeChange?: (size: number) => void;

  /** Show total info. true = "X to Y of Z", or custom render fn (default: false) */
  showTotal?: boolean | ((total: number, range: [number, number]) => React.ReactNode);

  /** Show first/last page jump buttons (default: true) */
  showFirstLast?: boolean;
  /** Max page number buttons (default: 5) */
  maxPageButtons?: number;

  /** "default" = full layout, "compact" = buttons only, right-aligned */
  variant?: "default" | "compact";
  /** Alignment of total info and size changer relative to page buttons.
   *  "spread" = info on left, buttons on right (full width).
   *  "end" = info + buttons grouped together on the right.
   *  Default: "spread" */
  align?: "spread" | "end";
  /** Disable all interactions */
  disabled?: boolean;
  /** Extra content rendered on the left side */
  toolbarExtra?: React.ReactNode;
}

export function Pagination({
  current,
  total,
  pageSize = 10,
  onChange,
  showSizeChanger = false,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  showTotal = false,
  showFirstLast = true,
  maxPageButtons = 5,
  variant = "default",
  align = "spread",
  disabled = false,
  toolbarExtra,
}: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  const startRow = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const endRow = Math.min(current * pageSize, total);

  const hasPrev = current > 1;
  const hasNext = current < totalPages;

  const pageNumbers = generatePageNumbers(current, totalPages, maxPageButtons);

  // `showTotal` shipped as a declared prop that no render branch ever read, so
  // every grid in the suite silently dropped it (MR-469). It renders here rather
  // than per-screen so one control answers "how many rows does this grid have"
  // everywhere. `variant="compact"` stays buttons-only by contract.
  const totalInfo = showTotal ? (
    typeof showTotal === "function" ? (
      showTotal(total, [startRow, endRow])
    ) : (
      <Typography as="span" variant="small" color="muted" className="whitespace-nowrap">
        {startRow} to {endRow} of {total}
      </Typography>
    )
  ) : null;

  const sizeChanger = showSizeChanger && onPageSizeChange ? (
    <Select
      value={pageSize.toString()}
      selectSize="sm"
      onChange={(value) => onPageSizeChange(Number(value))}
      disabled={disabled}
      options={pageSizeOptions.map((size) => ({
        value: size.toString(),
        label: `${size} per page`,
      }))}
    />
  ) : null;

  const pageButtons = (
    <div className="flex items-center gap-2">
      {showFirstLast && (
        <Button
          iconOnly
          startIcon={<ChevronsLeft className="w-4 h-4" />}
          variant="white"
          size="sm"
          onClick={() => onChange(1)}
          disabled={disabled || !hasPrev}
          aria-label="First page"
          type="button"
        />
      )}

      <Button
        iconOnly
        startIcon={<ChevronLeft className="w-4 h-4" />}
        variant="white"
        size="sm"
        onClick={() => onChange(current - 1)}
        disabled={disabled || !hasPrev}
        aria-label="Previous page"
        type="button"
      />

      <div className="flex items-center gap-1">
        {pageNumbers.map((pageNum) => (
          <Button
            key={pageNum}
            variant={current === pageNum ? "primary" : "white"}
            size="sm"
            onClick={() => onChange(pageNum)}
            disabled={disabled}
            aria-label={`Go to page ${pageNum}`}
            aria-current={current === pageNum ? "page" : undefined}
            type="button"
          >
            {pageNum}
          </Button>
        ))}
      </div>

      <Button
        iconOnly
        startIcon={<ChevronRight className="w-4 h-4" />}
        variant="white"
        size="sm"
        onClick={() => onChange(current + 1)}
        disabled={disabled || !hasNext}
        aria-label="Next page"
        type="button"
      />

      {showFirstLast && (
        <Button
          iconOnly
          startIcon={<ChevronsRight className="w-4 h-4" />}
          variant="white"
          size="sm"
          onClick={() => onChange(totalPages)}
          disabled={disabled || !hasNext}
          aria-label="Last page"
          type="button"
        />
      )}
    </div>
  );

  if (variant === "compact") {
    return (
      <div className="flex items-center justify-end">
        {pageButtons}
      </div>
    );
  }

  if (align === "end") {
    return (
      <div className="flex items-center justify-between gap-4">
        {toolbarExtra && (
          <div className="flex items-center gap-4">
            {toolbarExtra}
          </div>
        )}
        <div className="flex items-center gap-4 ml-auto shrink-0">
          {totalInfo}
          {sizeChanger}
          {pageButtons}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        {toolbarExtra}
        {totalInfo}
        {sizeChanger}
      </div>
      {pageButtons}
    </div>
  );
}
